import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'

import Header from './Header.js'
import Footer from './Footer.js'
import FilterToolbar from './FilterToolbar.js'
import FileView from './FileView.js'
import TreeView from './TreeView.js'
import { renderCustomIcons } from './common.js'

import redux from '../redux/index.js'
import { getTemplateManagerBaseUrl, getTemplateManagerLink, isSelected } from '../util.js'
import { isOutlook, isOutlookComposeMode } from '../lib/office.js'

const FILE_PANE_SET_TOP_HEIGHT_EVENT = 'pds-file-pane-set-top-height'
const PANE_SEPARATOR_HEIGHT = 3
const MIN_PANE_HEIGHT = 140

const ProjectPage = ({
  pdsId,
  onDocumentContextApplied,
  getPageItems,
  getSuggestionItems,
  setOrder,
  setOffset,
  setSearch,

  resetSelectedItem,

  pageItems,
  totalCount,
  limit,
  offset,
  orderBy,
  order,
  search,
  showFavorites,
  showActive,
  showRecent,

  onFilterFavoriteToggle,
  onFilterActiveToggle,
  onFilterRecentToggle,

  onSetSelectedItem,
  onToggleProjectFavorite,
  onClearFileView,
  onPersonWithRoleClick,
  selected,
  sharePointSaveEnabled,
  templateEnabled,
  templateManagerBaseUrl,
  locale
}) => {
  const initialPdsId = useRef(pdsId)
  useEffect(() => {
    const id = initialPdsId.current
    initialPdsId.current = null
    getPageItems(id)
    if (id) onDocumentContextApplied()
  }, [getPageItems, onDocumentContextApplied, limit, offset, orderBy, order, search, showFavorites, showActive, showRecent])
  useEffect(() => {
    if (isOutlook()) {
      getSuggestionItems()
    }
  }, [getSuggestionItems, showRecent])

  useEffect(() => {
    if (!isOutlook()) return
    const mailbox = Office.context.mailbox
    const itemChanged = Office.EventType?.ItemChanged
    const recipientsChanged = Office.EventType?.RecipientsChanged
    const onRecipientsChanged = () => getSuggestionItems({ preserveManualSelection: true })
    let composeItem = null
    const removeRecipientsHandler = () => {
      composeItem?.removeHandlerAsync?.(recipientsChanged, { handler: onRecipientsChanged })
      composeItem = null
    }
    const bindRecipientsHandler = () => {
      removeRecipientsHandler()
      const item = mailbox.item
      if (item?.addHandlerAsync && recipientsChanged && isOutlookComposeMode() &&
          Office.context.requirements?.isSetSupported('Mailbox', '1.7')) {
        composeItem = item
        item.addHandlerAsync(recipientsChanged, onRecipientsChanged)
      }
    }
    const onItemChanged = () => {
      bindRecipientsHandler()
      getSuggestionItems()
    }

    bindRecipientsHandler()
    if (mailbox.addHandlerAsync && itemChanged) {
      mailbox.addHandlerAsync(itemChanged, onItemChanged)
    }
    return () => {
      removeRecipientsHandler()
      if (itemChanged) mailbox.removeHandlerAsync?.(itemChanged, { handler: onItemChanged })
    }
  }, [getSuggestionItems])

  const checkSelected = isSelected(selected)

  const onFavoriteClick = (project) => {
    const clearFileView = (showFavorites &&
                           project &&
                           project.is_favorite &&
                           checkSelected(project))
    onToggleProjectFavorite(project)
    if (clearFileView) {
      onClearFileView()
    }
  }

  //
  // TODO move this dragging stuff to a separate component
  //

  const [isDragging, setIsDragging] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [origDbViewHeight, setOrigDbViewHeight] = useState(0)
  const [origFileViewHeight, setOrigFileViewHeight] = useState(0)
  const [dbViewHeight, setDbViewHeight] = useState(sharePointSaveEnabled ? '60%' : '100%')
  const [fileViewHeight, setFileViewHeight] = useState('40%')
  const [dragY, setDragY] = useState(0)

  const onMouseDown = (e) => {
    e.preventDefault()
    setIsDragging(true)
    setDragY(e.pageY)
    const dbView = document.getElementById('db-view-wrapper')
    setOrigDbViewHeight(dbView.clientHeight)
    const fileView = document.getElementById('file-view-wrapper')
    setOrigFileViewHeight(fileView.clientHeight)
  }

  const onMouseUp = (e) => setIsDragging(false)

  const onHeaderMenuOpen = (event) => setMenuAnchorEl(event.currentTarget)
  const onHeaderMenuClose = () => setMenuAnchorEl(null)

  const onMouseMove = (e) => {
    if (isDragging) {
      const diffY = e.pageY - dragY
      setDbViewHeight(origDbViewHeight + diffY)
      setFileViewHeight(origFileViewHeight - diffY)
    }
  }

  //
  // END dragging stuff
  //
  const pageCount = Math.ceil(totalCount / limit)
  const currentPage = pageCount > 0 ? Math.min(pageCount, Math.ceil(offset / limit) + 1) : 0
  const onPageChange = (nextPage) => {
    if (pageCount === 0) {
      setOffset(0)
      return
    }
    const boundedPage = Math.max(1, Math.min(pageCount, nextPage))
    setOffset((boundedPage - 1) * limit)
  }
  const onSortChange = () => {
    onClearFileView()
    setOrder(orderBy, order === 'asc' ? 'desc' : 'asc')
  }
  const onSearchChange = (str) => {
    if (search !== str) resetSelectedItem()
    setOffset(0)
    setSearch(str)
  }

  const title = locale.project_page_title

  useEffect(() => {
    if (offset < 0) {
      setOffset(0)
      return
    }
    if (pageCount === 0) {
      if (offset !== 0) {
        setOffset(0)
      }
      return
    }
    const maxOffset = (pageCount - 1) * limit
    if (offset > maxOffset) {
      setOffset(maxOffset)
    }
  }, [offset, limit, pageCount, setOffset])

  useEffect(() => {
    if (offset > 0 && pageItems.length === 0) {
      setOffset(Math.max(0, offset - limit))
    }
  }, [offset, limit, pageItems.length, setOffset])

  useEffect(() => {
    if (!sharePointSaveEnabled) {
      return
    }

    const onSetPaneHeight = (event) => {
      const requestedTopHeight = Number(event?.detail?.topHeight)
      if (!Number.isFinite(requestedTopHeight)) {
        return
      }

      const dbView = document.getElementById('db-view-wrapper')
      const fileView = document.getElementById('file-view-wrapper')
      if (!dbView || !fileView) {
        return
      }

      const totalHeight = Number(dbView.clientHeight) + Number(fileView.clientHeight) + PANE_SEPARATOR_HEIGHT
      const maxTopHeight = Math.max(MIN_PANE_HEIGHT, totalHeight - PANE_SEPARATOR_HEIGHT - MIN_PANE_HEIGHT)
      const topHeight = clampNumber(requestedTopHeight, MIN_PANE_HEIGHT, maxTopHeight)
      const bottomHeight = Math.max(MIN_PANE_HEIGHT, totalHeight - PANE_SEPARATOR_HEIGHT - topHeight)

      setDbViewHeight(topHeight)
      setFileViewHeight(bottomHeight)
    }

    window.addEventListener(FILE_PANE_SET_TOP_HEIGHT_EVENT, onSetPaneHeight)
    return () => {
      window.removeEventListener(FILE_PANE_SET_TOP_HEIGHT_EVENT, onSetPaneHeight)
    }
  }, [sharePointSaveEnabled])

  const onTemplateManagerClick = (item) => {
    const url = getTemplateManagerLink(templateManagerBaseUrl, item)
    if (url) {
      window.open(url, '_blank')
    }
  }
  const customIcons = renderCustomIcons({
    locale,
    onPersonWithRoleClick,
    onFavoriteClick,
    sharePointSaveEnabled,
    onTemplateManagerClick: templateEnabled && onTemplateManagerClick
  })

  return (
    <>
      <Header
        title={title}
        value={search}
        onSearchChange={onSearchChange}
        onLogoClick={onHeaderMenuOpen}
      />
      <FilterToolbar
        menuAnchorEl={menuAnchorEl}
        onMenuClose={onHeaderMenuClose}
        onFilterFavoriteToggle={onFilterFavoriteToggle}
        showFavorites={showFavorites}
        onFilterActiveToggle={onFilterActiveToggle}
        showActive={showActive}
        onFilterRecentToggle={onFilterRecentToggle}
        showRecent={showRecent}
        onSortChange={onSortChange}
        order={order}
        pageCount={pageCount}
        currentPage={currentPage}
        itemCount={pageItems.length}
        onPageChange={onPageChange}
        limit={limit}
      />
      <div
        style={contentWrapperStyle(sharePointSaveEnabled)}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <div id='db-view-wrapper' style={treeViewStyle(dbViewHeight)}>
          <TreeView
            items={pageItems}
            isSelected={checkSelected}
            scrollSelectedIntoView
            onItemSelected={(e, item) => onSetSelectedItem(item, sharePointSaveEnabled)}
            renderCustomIcons={customIcons}
          />
        </div>
        {sharePointSaveEnabled ? <div style={resizeSliderStyle} onMouseDown={onMouseDown} /> : null}
        {sharePointSaveEnabled ? <div id='file-view-wrapper' style={fileViewStyle(fileViewHeight)}><FileView /></div> : null}
      </div>
      <div style={footerStyle}><Footer /></div>
    </>
  )
}

// TODO refactor these styles, maybe use styled-components?

const border = '1px solid #a3a3a3'

const contentWrapperStyle = (sharePointSaveEnabled) => {
  return {
    position: 'absolute',
    top: 90,
    bottom: 50,
    width: '100%'
  }
}

const treeViewStyle = (height) => {
  return {
    background: 'white',
    overflow: 'auto',
    borderTop: border,
    borderLeft: border,
    borderRight: border,
    height
  }
}

const resizeSliderStyle = {
  width: '100%',
  height: 3,
  borderTop: border,
  borderLeft: border,
  borderRight: border,
  background: 'rgb(202, 221, 204)',
  cursor: 'ns-resize'
}

const fileViewStyle = (height) => {
  return {
    background: 'white',
    overflow: 'auto',
    height,
    border
  }
}

const footerStyle = {
  position: 'absolute',
  bottom: 0,
  width: '100%'
}

function clampNumber (value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0))
}

const mapStateToProps = (state) => {
  const {
    pageItems,
    totalCount,
    limit,
    offset,
    orderBy,
    order,
    search,
    showFavorites,
    showActive,
    showRecent
  } = state.project

  return {
    pageItems: pageItems.map(p => {
      const name = `${p.name} (${p.collection_name})`
      return { ...p, name }
    }),
    totalCount,
    limit,
    offset,
    orderBy,
    order,
    search,
    showFavorites,
    showActive,
    showRecent,

    selected: state.selected.current,
    locale: state.locale.strings,
    sharePointSaveEnabled: state.login.userData.sharePointSaveEnabled,
    templateEnabled: state.login.userData.sharePointTemplatesEnabled,
    templateManagerBaseUrl: getTemplateManagerBaseUrl(state.login.userData)
  }
}

const mapDispatchToProps = (dispatch) => {
  const {
    dialog,
    files,
    project,
    selected
  } = redux.actions
  return {
    getPageItems: (pdsId) => {
      dispatch(project.getPageItems(pdsId))
    },
    getSuggestionItems: (options) => {
      dispatch(project.getSuggestionItems(options))
    },
    setOrder: (orderBy, order) => dispatch(project.setOrder(orderBy, order)),
    setOffset: (offset) => dispatch(project.setOffset(offset)),
    resetSelectedItem: () => dispatch(selected.resetSelectedItem()),
    setSearch: (search) => {
      dispatch(project.setSearch(search))
    },

    onSetSelectedItem: (item, sharePointSaveEnabled) => {
      dispatch(selected.setSelectedItem(item))
      if (sharePointSaveEnabled) {
        dispatch(files.getRootFolder(item))
      }
    },
    onPersonWithRoleClick: (contactId, item) => {
      dispatch(dialog.showPeopleDialog(contactId, item.person_role))
    },
    onToggleProjectFavorite: (p) => {
      dispatch(project.toggleFavorite(p))
    },
    onClearFileView: () => {
      dispatch(selected.resetSelectedItem())
    },
    onFilterFavoriteToggle: () => {
      dispatch(selected.resetSelectedItem())
      dispatch(project.toggleShowFavorites())
    },
    onFilterRecentToggle: () => {
      dispatch(selected.resetSelectedItem())
      dispatch(project.toggleShowRecent())
    },
    onFilterActiveToggle: () => {
      dispatch(selected.resetSelectedItem())
      dispatch(project.toggleShowActive())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectPage)
